import Constants from '@/Constants';
import { usePrevious } from '@/lib/hooks';
import { CardMode } from '@/models/CardMode';
import { CourseRequest } from '@/models/CourseResquest';
import { CourseSectionConfiguration } from '@/models/CourseSectionConfiguration';
import { FilterOptionRequest } from '@/models/FilterOptionRequest';
import List from '@/models/List';
import { PaginationData } from '@/models/PaginationData';
import { SlideConfig } from '@/models/SlideConfig';
import { CourseViewType, ViewType } from '@/models/ViewType';
import { analyticsService, LMSEvents, PlaylistEvents } from '@/services/AnalyticsService';
import { courseService } from '@/services/CourseService';
import { lmsCourseService } from '@/services/LMSCourseService';
import { ApplicationState } from '@/store';
import { coursesRequest, setCourseForInsertion } from '@/store/list/action';
import { CourseStudentView, ListCourseStudentViewData, ListTypes } from '@/store/list/types';
import '@/styles/variables.scss';
import React, { useEffect } from 'react';
import MagicSliderDots from 'react-magic-slider-dots';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useParams } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import _ from 'underscore';
import CourseCard from '../CourseCard';
import LMSCourseCard from '../LMSCourseCard';
import './style.scss';

interface Props {
    viewType: ViewType | CourseViewType;
    mode: CardMode;
    slideConfiguration?: SlideConfig;
    filteredVisibilityOption?: FilterOptionRequest;
    localesToFilter?: string;
}

const getEventNameByType = (type: string, moduleId: number, categoryId?: string) => {
    if (type.startsWith(Constants.listCategorizedPrefix) && categoryId) {
        if (moduleId === Constants.PLAYLIST_MODULE_ID) {
            return PlaylistEvents.ACCESS_PLAYLIST_SECTION_IN_CATEGORY.replace('%n', categoryId);
        }
        return LMSEvents.ACCESS_COURSE_SECTION_IN_CATEGORY.replace('%n', categoryId);
    }

    switch (type) {
        case 'listLastPublishedStudentView':
            if (moduleId === Constants.PLAYLIST_MODULE_ID) {
                return PlaylistEvents.ACCESS_PLAYLIST_SECTION_LAST_PUBLISHED;
            }
            return LMSEvents.ACCESS_COURSE_SECTION_LAST_PUBLISHED;
        case 'listMostAccessedStudentView':
            if (moduleId === Constants.PLAYLIST_MODULE_ID) {
                return PlaylistEvents.ACCESS_PLAYLIST_SECTION_MOST_ACCESSED;
            }
            return LMSEvents.ACCESS_COURSE_SECTION_MOST_ACCESSED;
        case 'listInProgressStudentView':
            if (moduleId === Constants.PLAYLIST_MODULE_ID) {
                return PlaylistEvents.ACCESS_PLAYLIST_SECTION_IN_PROGRESS;
            }
            return LMSEvents.ACCESS_COURSE_SECTION_IN_PROGRESS;
        case 'listCompletedStudentView':
            if (moduleId === Constants.PLAYLIST_MODULE_ID) {
                return PlaylistEvents.ACCESS_PLAYLIST_SECTION_MY_COMPLETED;
            }
            return LMSEvents.ACCESS_COURSE_SECTION_MY_COMPLETED;
        case 'listTopRatedStudentView':
            if (moduleId === Constants.PLAYLIST_MODULE_ID) {
                return PlaylistEvents.ACCESS_PLAYLIST_SECTION_TOP_RATED;
            }
            return LMSEvents.ACCESS_COURSE_SECTION_TOP_RATED;
        case 'listFavoriteStudentView':
            if (moduleId === Constants.PLAYLIST_MODULE_ID) {
                return PlaylistEvents.ACCESS_PLAYLIST_SECTION_MY_FAVORITES;
            }
            return LMSEvents.ACCESS_COURSE_SECTION_MY_FAVORITES;
        case 'listSearchResultStudentView':
            if (moduleId === Constants.PLAYLIST_MODULE_ID) {
                return PlaylistEvents.ACCESS_PLAYLIST_SECTION_SEARCH_RESULT;
            }
            return LMSEvents.ACCESS_COURSE_SECTION_SEARCH_RESULT;
        case 'listCoursesHavingAContentStudentView':
            return PlaylistEvents.ACCESS_PLAYLIST_SECTION_HAS_A_CONTENT;
        case 'listPublishedUserContentsStudentView':
            return PlaylistEvents.ACCESS_PLAYLIST_SECTION_STUDENT_PUBLISHED;
        case 'listMyCourseStudentView':
            return PlaylistEvents.ACCESS_PLAYLIST_SECTION_MY_CREATIONS;
        case 'listEssentialStudentView':
            return LMSEvents.ACCESS_COURSE_SECTION_ESSENTIALS;
        case 'listTrainingsEnrolledStudentView':
            return LMSEvents.ACCESS_COURSE_SECTION_TRAINING;
        default:
            if (moduleId === Constants.PLAYLIST_MODULE_ID) {
                return PlaylistEvents.ACCESS_PLAYLIST;
            }
            return LMSEvents.ACCESS_COURSE;
    }
};

const logEvent = (entityId: number, entityName: string, type: string, moduleId: number, categoryId?: string) => {
    analyticsService.logEvent(getEventNameByType(type, moduleId, categoryId), {
        entityId,
        entityName,
        moduleId,
        entityType: Constants.entityTypes.COURSE,
    });
    analyticsService.logEvent(getEventNameByType('default', moduleId, categoryId), {
        entityId,
        entityName,
        moduleId,
        entityType: Constants.entityTypes.COURSE,
    });
};

const CourseSection: React.FC<Props> = (props: Props) => {
    const { viewType, mode, slideConfiguration, filteredVisibilityOption, localesToFilter } = props;

    const SLIDES_TO_SHOW: number = slideConfiguration ? slideConfiguration.slidesToShow : 4;
    const SLIDES_TO_SCROLL: number = slideConfiguration ? slideConfiguration.slidesToScroll : 4;

    const { id: categoryIdParam } = useParams<{ id: string }>();
    const { id: personId } = useSelector((state: ApplicationState) => state.identity.data.person);
    const { content } = useSelector((state: ApplicationState) => state.itens.currentData);
    const { course } = useSelector((state: ApplicationState) => state.lists.currentData);

    const { data: strings } = useSelector((state: ApplicationState) => state.locale);
    const { listStudentView, listSize, listSizeTotal, favoriteCourseList, currentContent, modules, publicPersonId } = useSelector(
        (state: ApplicationState) => {
            const courseStudentViewList: ListCourseStudentViewData = state.lists.listData[viewType];
            return {
                publicPersonId: state.people.publicPerson && state.people.publicPerson.id,
                listStudentView: courseStudentViewList.list,
                listSize: courseStudentViewList.list.length,
                listSizeTotal: courseStudentViewList.total,
                favoriteCourseList: courseService.getFavoriteEntityListAsIdsString(state.favorite.favoriteList, Constants.entityTypes.COURSE),
                currentContent: state.itens.currentData.content,
                modules: state.appOrganization.data.modules,
            };
        },
    );

    const { selectedCourseForInsertion } = useSelector((state: ApplicationState) => ({
        selectedCourseForInsertion: state.lists.selectedCourseForInsertion,
    }));

    const { term: searchTerm } = useSelector((state: ApplicationState) => state.search.data);

    const prevFavoriteCourseList = usePrevious(favoriteCourseList);

    const dispatch = useDispatch();

    const location = useLocation();

    const isContentInCourse = (course: List): boolean => {
        if (viewType === 'listMyCourseStudentView' && mode === 'selection') {
            return courseService.isContentInCourse(course, currentContent);
        }
        return false;
    };

    const getCoursesRequest = (): void => {
        dispatch(coursesRequest(configuration.requestData));
    };

    const getCourseConfiguration = (): CourseSectionConfiguration => {
        let auxViewType!: ViewType | CourseViewType;

        let categoryId = undefined;

        if (viewType.startsWith(Constants.listCategorizedPrefix)) {
            auxViewType = 'listOfCategories';
            categoryId = categoryIdParam;
        }

        const {
            type,
            orderBy,
            courseStatus,
            lessonStatus,
            courseIds,
            doRequest,
            contentId,
            courseId,
            title,
            term,
            excludeEnrollments,
            onlyMine,
            moduleIds,
            ownerId,
            ignoreLocale,
            excludeExpiredAndNotFinished,
            section,
            visibility,
            locales,
        } = {
            listLastPublishedStudentView: {
                type: ListTypes.LAST_PUBLISHED_SUCCESS,
                orderBy: 'pc.createdOn desc',
                excludeExpiredAndNotFinished: true,
                moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.PLAYLIST_MODULE_ID, Constants.LMS_MODULE_ID]),
                doRequest: true,
            },
            listMostAccessedStudentView: {
                type: ListTypes.MOST_ACCESSED_SUCCESS,
                orderBy: 'c.amountConsumed desc',
                moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.PLAYLIST_MODULE_ID]),
                doRequest: true,
            },
            listInProgressStudentView: {
                type: ListTypes.IN_PROGRESS_SUCCESS,
                orderBy: 'cen.startDate desc',
                courseStatus: Constants.courseEnrollmentStatus.STARTED,
                lessonStatus: Constants.contentEnrollmentStatus.INCOMPLETE,
                excludeExpiredAndNotFinished: true,
                section: 'IN_PROGRESS',
                moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.PLAYLIST_MODULE_ID, Constants.LMS_MODULE_ID]),
                doRequest: true,
            },
            listCompletedStudentView: {
                type: ListTypes.COMPLETED_SUCCESS,
                orderBy: 'cen.startDate desc',
                courseStatus: `${Constants.courseEnrollmentStatus.COMPLETED},${Constants.courseEnrollmentStatus.PASSED},${Constants.courseEnrollmentStatus.FAILED}`,
                moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.PLAYLIST_MODULE_ID, Constants.LMS_MODULE_ID]),
                doRequest: true,
            },
            listTopRatedStudentView: {
                type: ListTypes.TOP_RATED_SUCCESS,
                orderBy: 'c.rating desc',
                moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.PLAYLIST_MODULE_ID]),
                doRequest: true,
            },
            listFavoriteStudentView: {
                type: ListTypes.FAVORITES_SUCCESS,
                orderBy: 'c.views desc',
                courseIds: favoriteCourseList,
                doRequest: favoriteCourseList.length > 0,
                moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.PLAYLIST_MODULE_ID, Constants.LMS_MODULE_ID]),
            },
            listOfCategories: {
                type: ListTypes.CATEGORIZED_SUCCESS,
                orderBy: 'c.views desc',
                categoryId,
                excludeExpiredAndNotFinished: true,
                moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.PLAYLIST_MODULE_ID, Constants.LMS_MODULE_ID]),
                doRequest: true,
            },
            listForYouStudentView: {
                type: ListTypes.FOR_YOU_SUCCESS,
                orderBy: '',
                courseIds: '',
                moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.PLAYLIST_MODULE_ID]),
                doRequest: false,
            },
            listLikeYouStudentView: {
                type: ListTypes.LIKE_YOU_SUCCESS,
                orderBy: '',
                courseIds: '',
                moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.PLAYLIST_MODULE_ID]),
            },
            listCoursesHavingAContentStudentView: {
                type: ListTypes.COURSES_HAVING_A_CONTENT_SUCCESS,
                title: listStudentView.length > 1 ? strings.LISTS_WITH_THIS_CONTENT : strings.LIST_WITH_THIS_CONTENT,
                orderBy: 'c.rating desc',
                doRequest: true,
                contentId: content && content.id,
                courseId: courseService.isCourseContent() ? course.id : undefined,
                moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.PLAYLIST_MODULE_ID]),
            },
            listSearchResultStudentView: {
                type: ListTypes.SEARCHED_SUCCESS,
                listTypeParm: 'SEARCH',
                term: searchTerm,
                orderBy: '',
                doRequest: true,
                moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.PLAYLIST_MODULE_ID, Constants.LMS_MODULE_ID]),
                locales: localesToFilter,
            },
            listMyCourseStudentView: {
                type: ListTypes.MY_COURSES_SUCCESS,
                excludeEnrollments: true,
                onlyMine: true,
                orderBy: 'pc.createdOn desc',
                doRequest: true,
                moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.PLAYLIST_MODULE_ID]),
                ignoreLocale: location.pathname === '/mine/mycreations',
                visibility: filteredVisibilityOption,
            },
            listEssentialStudentView: {
                type: ListTypes.ESSENTIAL_SUCCESS,
                doRequest: true,
                moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.LMS_MODULE_ID]),
                section: 'MANDATORY',
            },
            listSuggestedStudentView: {
                type: '',
            },
            listPublishedUserContentsStudentView: {
                type: ListTypes.PUBLISHED_USER_CONTENTS_SUCCESS,
                title: strings.PUBLISHED_CONTENTS,
                ownerId: publicPersonId,
                orderBy: 'c.views desc',
                moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.PLAYLIST_MODULE_ID]),
                doRequest: true,
            },
            listTrainingsEnrolledStudentView: {
                type: ListTypes.TRAININGS_SUCCESS,
                doRequest: true,
                moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.LMS_MODULE_ID]),
                section: 'TRAINING',
            },
        }[auxViewType || viewType];

        let paginationData: PaginationData = {
            offset: listSize,
            limit: Constants.COURSE_SECTION_PAGINATION_LIMIT,
            orderBy,
            doRequest,
            courseRequestSuccessType: type,
        } as PaginationData;

        if (viewType === 'listFavoriteStudentView') {
            if (prevFavoriteCourseList !== favoriteCourseList) {
                paginationData = { ...paginationData, offset: 0 };
            }
        }

        return {
            type,
            title,
            requestData: {
                paginationData,
                personId,
                courseStatus,
                lessonStatus,
                courseIds,
                categoryId,
                courseId,
                contentId,
                term,
                excludeEnrollments,
                onlyMine,
                moduleIds,
                ownerId,
                ignoreLocale,
                excludeExpiredAndNotFinished,
                section,
                visibility,
                locales,
            } as CourseRequest,
        };
    };

    const configuration = getCourseConfiguration();

    const handleLoadMoreOnAfterChange = (currentSlide: number) => {
        if (listSizeTotal <= SLIDES_TO_SCROLL || currentSlide === 0 || currentSlide < listSize - SLIDES_TO_SCROLL) {
            return;
        }

        const slideToLoadMore: number = listSize / 2;
        if (listSize > SLIDES_TO_SCROLL && currentSlide >= slideToLoadMore) {
            if (listSize < listSizeTotal) {
                getCoursesRequest();
            }
        }
    };

    useEffect(() => {
        if (viewType === 'listPublishedUserContentsStudentView') {
            if (!!categoryIdParam && Number(categoryIdParam) === publicPersonId) {
                getCoursesRequest();
            }
        } else getCoursesRequest();
    }, [viewType, categoryIdParam, publicPersonId]);

    useEffect(() => {
        if (viewType === 'listSearchResultStudentView') {
            dispatch(
                coursesRequest({
                    ...configuration.requestData,
                    paginationData: {
                        ...configuration.requestData.paginationData,
                        offset: 0,
                    },
                }),
            );
        }
    }, [localesToFilter]);

    const getCarouselDots = (dots: boolean) => {
        return <MagicSliderDots dotWidth={30} dots={dots} numDotsToShow={4} />;
    };

    const settings = {
        afterChange: (currentSlide: number) => {
            handleLoadMoreOnAfterChange(currentSlide);
        },
        dots: true,
        infinite: false,
        speed: 500,
        slidesToShow: SLIDES_TO_SHOW,
        slidesToScroll: SLIDES_TO_SCROLL,
        appendDots: (dots: boolean) => {
            return getCarouselDots(dots);
        },
    };

    const onSelectCourseHandler = (course: List): void => {
        if (mode === 'selection') {
            if (selectedCourseForInsertion && selectedCourseForInsertion.id === course.id) {
                if (isContentInCourse(course)) {
                    return;
                }
                dispatch(setCourseForInsertion(null));
            } else {
                dispatch(setCourseForInsertion(course));
            }
        } else {
            logEvent(course.id as number, course.name, viewType, course.moduleId as number, categoryIdParam);
        }
    };

    const isLmsCardDisabled = (status: string, isExpired: boolean): boolean =>
        Boolean(isExpired && !lmsCourseService.isCourseCompletedPassedOrFailed(status));

    return (
        <>
            {listStudentView && listStudentView.length > 0 && (
                <>
                    {configuration.title && (
                        <div className="carrosell-title-container">
                            <h2>{configuration.title}</h2>
                        </div>
                    )}
                    <div className="carrosell-slide-container">
                        <Slider {...settings}>
                            {listStudentView.map((courseStudentView: CourseStudentView) => {
                                if (courseStudentView && courseStudentView.course && courseStudentView.course.id) {
                                    const lmsCardDisabled = isLmsCardDisabled(
                                        courseStudentView.courseEnrollment?.status,
                                        courseStudentView.programClass.isExpired,
                                    );
                                    return (
                                        <div key={_.uniqueId('courseCard_')}>
                                            {courseStudentView.course.moduleId === Constants.PLAYLIST_MODULE_ID && (
                                                <Link
                                                    className="link-to-course"
                                                    onClick={() => onSelectCourseHandler(courseStudentView.course)}
                                                    to={mode === 'view' ? `/${Constants.COURSES_RESOURCE_NAME}/${courseStudentView.course.id}` : '#'}
                                                >
                                                    <CourseCard
                                                        course={courseStudentView.course}
                                                        courseEnrollment={courseStudentView.courseEnrollment}
                                                        hasCurrentContent={isContentInCourse(courseStudentView.course)}
                                                        mode={mode}
                                                        totalRating={courseService.getTotalRating(courseStudentView.course)}
                                                    />
                                                </Link>
                                            )}
                                            {courseStudentView.course.moduleId === Constants.LMS_MODULE_ID && (
                                                <Link
                                                    className="link-to-course"
                                                    onClick={() => !lmsCardDisabled && onSelectCourseHandler(courseStudentView.course)}
                                                    to={
                                                        !lmsCardDisabled && mode === 'view'
                                                            ? `/lms/enrollments/${courseStudentView.classEnrollment.id}/courses/${courseStudentView.course.id}`
                                                            : '#'
                                                    }
                                                >
                                                    <LMSCourseCard
                                                        course={courseStudentView.course}
                                                        courseEnrollment={courseStudentView.courseEnrollment}
                                                        disabled={lmsCardDisabled}
                                                        hasCurrentContent={isContentInCourse(courseStudentView.course)}
                                                        mode={mode}
                                                        programClass={courseStudentView.programClass}
                                                    />
                                                </Link>
                                            )}
                                        </div>
                                    );
                                }
                            })}
                        </Slider>
                    </div>
                </>
            )}
        </>
    );
};

export default CourseSection;
